/*
  GEMDRAW — Band motion

  The entrance and idle system for the three promotional bands: the mystery
  drop, the campaign reveal and the overdrive vault.

  WHY THIS IS NOT THE REVEAL ENGINE. gemdraw-reveal.js is one observer for the
  whole page that marks each element once and gets out of the way. That is the
  right tool for a grid of products and the wrong one here, because these three
  bands need something it deliberately does not do: a per-section timeline that
  starts when THAT section arrives, and an idle state that runs only while the
  section is on screen and stops when it leaves. Bolting a second mode onto the
  page-wide engine would have made every other section pay for it.

  THREE LANGUAGES, ONE COMPONENT. The bands share most of their markup, so the
  themes are a data attribute rather than three copies of anything:

    data-motion-section="mystery"    masks, seals, things emerging
    data-motion-section="campaign"   documents, counters, verification
    data-motion-section="overdrive"  plates, weight, mechanical seating

  This file knows none of that. It writes three classes and the stylesheet
  decides what they mean, which is why adding a fourth language later needs no
  JavaScript at all.

  THE THREE STATES, AND THE ORDER MATTERS.

    .is-motion-ready  this file is running and has taken responsibility
    .is-revealed      the entrance has played — once, ever
    .is-in-view       the section is on screen, so idles may run

  CONTENT IS NEVER HIDDEN BY DEFAULT. Every starting state in the stylesheet is
  behind .is-motion-ready, which only exists once this script has bound the
  section. No script, a parse error, an old browser, reduced motion — every one
  of those paths ends with the band fully visible and completely readable. That
  is the single most important line in this file.

  NO LIBRARIES. One observer per band, three class names, and CSS.
*/

(function () {
  'use strict';

  const SECTION = '[data-motion-section]';

  const READY = 'is-motion-ready';
  const REVEALED = 'is-revealed';
  const IN_VIEW = 'is-in-view';

  /*
    A quarter of the band has to be on screen before the timeline starts, and
    the bottom tenth of the viewport does not count — so the entrance begins as
    the band is being arrived at rather than as its first pixel appears. This is
    also what stops three bands on one page from firing together on load.
  */
  const OBSERVER_OPTIONS = {
    threshold: [0, 0.28],
    rootMargin: '0px 0px -10% 0px',
  };

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* Cleanups, keyed by the section element itself so a removed section takes
     its own entry with it and nothing has to be reference-counted. */
  const bound = new WeakMap();
  const live = [];

  function reveal(el) {
    if (el.classList.contains(REVEALED)) return;
    el.classList.add(REVEALED);
  }

  function bind(el) {
    if (bound.has(el)) return;

    /*
      REDUCED MOTION NEVER GETS THE READY CLASS.

      Without it not one starting state applies, so the band renders exactly as
      it would with this file absent: everything in place, nothing transparent,
      nothing offset. The stylesheet's own reduced-motion block is the belt to
      this brace — either alone is enough.
    */
    if (reduceMotion.matches) {
      bound.set(el, { destroy: function () {} });
      return;
    }

    el.classList.add(READY);

    /*
      NO OBSERVER, NO PROBLEM. An old browser gets the whole band at once
      rather than a timeline, which is a downgrade in polish and not in
      content.
    */
    if (!('IntersectionObserver' in window)) {
      reveal(el);
      el.classList.add(IN_VIEW);
      bound.set(el, { destroy: function () {} });
      return;
    }

    const io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        /*
          Two jobs from one callback. The entrance is a one-way door — it fires
          the first time enough of the band is on screen and never again, even
          after the visitor scrolls away and back. The idle class is the
          opposite: it tracks presence, so the loops stop the moment the band
          leaves and resume from where the stylesheet says when it returns.
        */
        if (entry.intersectionRatio >= 0.28) reveal(entry.target);

        if (entry.isIntersecting) {
          entry.target.classList.add(IN_VIEW);
        } else {
          entry.target.classList.remove(IN_VIEW);
        }
      });
    }, OBSERVER_OPTIONS);

    io.observe(el);

    /*
      THE GUARD, AND IT IS NOT OPTIONAL.

      An observer reports threshold crossings. A band that is already on screen
      at load, or one the visitor jumps past with an anchor link or a restored
      scroll position, can fail to cross anything — and with the ready class on,
      an unrevealed band is an invisible band. Every starting state in this
      system is therefore backed by a timer that reveals unconditionally.

      A second and a half is long enough that it never pre-empts a real
      intersection and short enough that nobody sees a gap.
    */
    const guard = window.setTimeout(function () {
      reveal(el);
    }, 1500);

    bound.set(el, {
      destroy: function () {
        window.clearTimeout(guard);
        io.disconnect();
      },
    });

    live.push(el);
  }

  function init(root) {
    const scope = root || document;

    scope.querySelectorAll(SECTION).forEach(bind);

    /* A section re-rendered by the editor arrives as its own root and is not
       found by a descendant query. */
    if (scope !== document && scope.matches && scope.matches(SECTION)) bind(scope);
  }

  function destroy(el) {
    const entry = bound.get(el);
    if (!entry) return;
    entry.destroy();
    bound.delete(el);
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      function () {
        init();
      },
      { once: true }
    );
  } else {
    init();
  }

  /* The theme editor replaces a section's markup wholesale. The new nodes carry
     no state, so binding the new subtree is all that is needed. */
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  /*
    Observers are not garbage collected while they hold a live target, and the
    editor re-renders a section dozens of times in a session. Every detached
    band releases its observer and its guard here.
  */
  document.addEventListener('shopify:section:unload', function () {
    for (let i = live.length - 1; i >= 0; i -= 1) {
      if (document.contains(live[i])) continue;
      destroy(live[i]);
      live.splice(i, 1);
    }
  });

  /*
    A visitor turning reduced motion on mid-session gets the finished band
    rather than whatever frame it happened to be on: the ready class comes off,
    which drops every starting state at once, and the observers stop.
  */
  if (typeof reduceMotion.addEventListener === 'function') {
    reduceMotion.addEventListener('change', function () {
      if (!reduceMotion.matches) return;

      live.forEach(function (el) {
        destroy(el);
        el.classList.remove(READY, IN_VIEW);
      });
      live.length = 0;
    });
  }
})();
